/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useCallback, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import Button from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import Form, {
	ErrorMessage,
	Field,
	Fieldset,
	MessageWrapper,
	RequiredAsterisk,
} from '@atlaskit/form';
import Link from '@atlaskit/link';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import SectionMessage from '@atlaskit/section-message';
import Select from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import { currentDisplayedTierAtom } from '../../../ui/state';

import {
	ATTACHMENT_FIELD_ID,
	CURRENT_VIEW_FIELD_ID,
	CURRENT_VIEW_LOGGED_IN_VALUE_ID,
	CURRENT_VIEW_LOGGED_OUT_VALUE_ID,
	VIEW_VERSION_FIELD_ID,
	getViewVersionValueId,
} from './feedbackFields';
import { messages } from './messages';

type SelectValue = 'bug' | 'comment' | 'suggestion' | 'question' | 'empty';

interface OptionType {
	label: string;
	value: SelectValue;
}

export interface CustomFeedbackCollectorProps {
	atlassianAccountId: string;
	entrypointId: string;
	name: string | undefined;
	isLoggedIn: boolean;
	onClose: () => void;
	onSubmit: () => void;
}

const FEEDBACK_COLLECTOR_BASE_PATH = '/gateway/api/feedback-collector-api';
const ATTACHMENT_ENDPOINT = '/v2/attachment';

function truncateSingleLine(text: string, maxLength: number): string {
	const singleLine = text.replace(/\n/g, ' ');
	return singleLine.length <= maxLength ? singleLine : `${singleLine.slice(0, maxLength - 1)}…`;
}

function getTypeFieldValueId(type: SelectValue): string {
	switch (type) {
		case 'bug':
			return '10105';
		case 'comment':
			return '10106';
		case 'suggestion':
			return '10107';
		case 'question':
			return '10108';
		case 'empty':
		default:
			return 'empty';
	}
}

function extractAttachmentIds(payload: unknown): string[] {
	if (Array.isArray(payload)) {
		if (payload.every((value): value is string => typeof value === 'string')) {
			return payload;
		}

		return payload
			.map((value) => {
				if (
					typeof value === 'object' &&
					value !== null &&
					'id' in value &&
					typeof value.id === 'string'
				) {
					return value.id;
				}
				return undefined;
			})
			.filter((value): value is string => Boolean(value));
	}

	if (typeof payload !== 'object' || payload === null) {
		return [];
	}

	if (
		'attachmentIds' in payload &&
		Array.isArray(payload.attachmentIds) &&
		payload.attachmentIds.every((value): value is string => typeof value === 'string')
	) {
		return payload.attachmentIds;
	}

	if (
		'attachments' in payload &&
		Array.isArray(payload.attachments) &&
		payload.attachments.every(
			(value): value is { id: string } =>
				typeof value === 'object' &&
				value !== null &&
				'id' in value &&
				typeof value.id === 'string',
		)
	) {
		return payload.attachments.map((attachment) => attachment.id);
	}

	return [];
}

async function uploadAttachments(files: File[], entrypointId: string): Promise<string[]> {
	if (files.length === 0) {
		return [];
	}

	const entrypointQueryParam = `entrypointId=${encodeURIComponent(entrypointId)}`;

	const formData = new FormData();
	files.forEach((file) => formData.append('files', file, file.name));

	const response = await fetch(
		`${FEEDBACK_COLLECTOR_BASE_PATH}${ATTACHMENT_ENDPOINT}?${entrypointQueryParam}`,
		{
			method: 'POST',
			body: formData,
			credentials: 'include',
		},
	);

	if (!response.ok) {
		return [];
	}

	const rawPayload = await response.text();
	if (!rawPayload) {
		return [];
	}

	try {
		return extractAttachmentIds(JSON.parse(rawPayload));
	} catch {
		return [];
	}
}

const requiredSummaryStyles = css({
	color: token('color.text.subtle'),
	marginBottom: token('space.300'),
});

const attachmentListStyles = css({
	listStyle: 'none',
	padding: 0,
	marginTop: token('space.100'),
});

const attachmentItemStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	marginBottom: token('space.050'),
});

// ---------------------------------------------------------------------------
// CustomFeedbackCollector
//
// Mirrors the structure and styling of @atlaskit/feedback-collector's
// FeedbackForm, adding:
//   1. Image / file attachment upload.
//   2. Two hidden context fields: currentView and viewVersion.
// ---------------------------------------------------------------------------
export const CustomFeedbackCollector = ({
	atlassianAccountId,
	entrypointId,
	name,
	isLoggedIn,
	onClose,
	onSubmit,
}: CustomFeedbackCollectorProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const currentDisplayedTier = useAtomValue(currentDisplayedTierAtom);

	const [type, setType] = useState<SelectValue>('empty');
	const [description, setDescription] = useState('');
	const [canBeContacted, setCanBeContacted] = useState(false);
	const [enrollInResearchGroup, setEnrollInResearchGroup] = useState(false);
	const [attachments, setAttachments] = useState<File[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [validationErrors, setValidationErrors] = useState<{
		type?: string;
		description?: string;
	}>({});

	const fileInputRef = useRef<HTMLInputElement>(null);
	const focusRef = useRef<HTMLElement>(null);

	const isAnonymous = !atlassianAccountId;
	const isTypeSelected = type !== 'empty';

	const selectOptions: OptionType[] = [
		{ label: formatMessage(messages.typeQuestion), value: 'question' },
		{ label: formatMessage(messages.typeComment), value: 'comment' },
		{ label: formatMessage(messages.typeBug), value: 'bug' },
		{ label: formatMessage(messages.typeSuggestion), value: 'suggestion' },
	];

	const fieldLabels: Record<SelectValue, string> = {
		bug: formatMessage(messages.fieldLabelBug),
		comment: formatMessage(messages.fieldLabelComment),
		suggestion: formatMessage(messages.fieldLabelSuggestion),
		question: formatMessage(messages.fieldLabelQuestion),
		empty: formatMessage(messages.fieldLabelEmpty),
	};

	// -------------------------------------------------------------------------
	// View context fields
	// -------------------------------------------------------------------------
	const buildViewFields = useCallback((): Array<{ id: string; value: { id: string } }> => {
		const currentViewValueId = isLoggedIn
			? CURRENT_VIEW_LOGGED_IN_VALUE_ID
			: CURRENT_VIEW_LOGGED_OUT_VALUE_ID;

		const fields: Array<{ id: string; value: { id: string } }> = [
			{ id: CURRENT_VIEW_FIELD_ID, value: { id: currentViewValueId } },
		];

		if (isLoggedIn && currentDisplayedTier) {
			fields.push({
				id: VIEW_VERSION_FIELD_ID,
				value: { id: getViewVersionValueId(currentDisplayedTier) },
			});
		}

		return fields;
	}, [isLoggedIn, currentDisplayedTier]);

	// -------------------------------------------------------------------------
	// File handling
	// -------------------------------------------------------------------------
	const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files ?? []);
		setAttachments((prev) => [...prev, ...files]);
		if (event.target) {
			event.target.value = '';
		}
	}, []);

	const removeAttachment = useCallback((index: number) => {
		setAttachments((prev) => prev.filter((_, i) => i !== index));
	}, []);

	// -------------------------------------------------------------------------
	// Validation
	// -------------------------------------------------------------------------
	const validate = useCallback((): { type?: string; description?: string } => {
		const errors: { type?: string; description?: string } = {};
		if (type === 'empty') {
			errors.type = formatMessage(messages.validationTypeRequired);
		}
		if (!description) {
			errors.description = formatMessage(messages.validationDescriptionRequired);
		}
		return errors;
	}, [type, description, formatMessage]);

	// -------------------------------------------------------------------------
	// Submission
	// -------------------------------------------------------------------------
	const postFeedback = useCallback(async (): Promise<void> => {
		if (!entrypointId) {
			return;
		}

		const summary = truncateSingleLine(description, 100);
		const viewFields = buildViewFields();
		const uploadedAttachmentIds = await uploadAttachments(attachments, entrypointId);
		const attachmentField = {
			id: ATTACHMENT_FIELD_ID,
			value: uploadedAttachmentIds.length > 0 ? uploadedAttachmentIds : undefined,
		};
		const fields = [
			{ id: 'customfield_10042', value: { id: getTypeFieldValueId(type) } },
			{ id: 'summary', value: summary },
			{ id: 'description', value: description },
			{ id: 'aaidOrHash', value: canBeContacted ? atlassianAccountId : undefined },
			{ id: 'customfield_10045', value: name ?? 'unknown' },
			{
				id: 'customfield_10043',
				value: canBeContacted ? [{ id: '10109' }] : [{ id: '10111' }],
			},
			{
				id: 'customfield_10044',
				value: enrollInResearchGroup ? [{ id: '10110' }] : [{ id: '10112' }],
			},
			...viewFields,
			attachmentField,
		].filter((f) => f.value !== undefined);

		const body = {
			feedback: {
				entrypointId,
				entrypointUiVersion: 'twg-microsite-custom',
				fields,
			},
		};

		const postData = btoa(JSON.stringify(body));
		fetch(`${FEEDBACK_COLLECTOR_BASE_PATH}/v2/feedback`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ data: postData }),
			credentials: 'include',
		}).catch(() => {
			// Fire-and-forget; surface submission errors silently.
		});

		onClose();
		setTimeout(onSubmit, 700);
	}, [
		attachments,
		atlassianAccountId,
		buildViewFields,
		canBeContacted,
		description,
		enrollInResearchGroup,
		entrypointId,
		name,
		onClose,
		onSubmit,
		type,
	]);

	return (
		<Modal
			shouldCloseOnOverlayClick={false}
			onClose={onClose}
			testId="customFeedbackCollectorModalDialog"
			shouldScrollInViewport
		>
			<Form
				onSubmit={async () => {
					const errors = validate();
					if (Object.keys(errors).length > 0) {
						setValidationErrors(errors);
						return;
					}
					setIsSubmitting(true);
					try {
						await postFeedback();
					} finally {
						setIsSubmitting(false);
					}
				}}
			>
				<ModalHeader hasCloseButton>
					<ModalTitle>
						<FormattedMessage {...messages.feedbackTitle} />
					</ModalTitle>
				</ModalHeader>

				<ModalBody>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p css={requiredSummaryStyles}>
						<FormattedMessage {...messages.requiredFieldsSummary} />
						<RequiredAsterisk />
					</p>

					{/* Feedback type selector */}
					<Field name="topic" label={formatMessage(messages.selectFeedbackLabel)} isRequired>
						{({ fieldProps: { id, ...restProps } }) => (
							<Fragment>
								<Select<OptionType>
									{...restProps}
									onChange={(option) => {
										if (!option || option instanceof Array) {
											return;
										}
										setType(option.value);
										if (validationErrors.type) {
											setValidationErrors((prev) => ({ ...prev, type: undefined }));
										}
									}}
									menuPortalTarget={getDocument()?.body ?? null}
									// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
									styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
									options={selectOptions}
									// @ts-ignore
									ref={focusRef}
									placeholder={formatMessage(messages.selectFeedbackPlaceholder)}
									inputId={id}
								/>
								{fg('twg_microsite_accessibility_improvements') ? (
									<MessageWrapper>
										{validationErrors.type && <ErrorMessage>{validationErrors.type}</ErrorMessage>}
									</MessageWrapper>
								) : (
									validationErrors.type && <ErrorMessage>{validationErrors.type}</ErrorMessage>
								)}
							</Fragment>
						)}
					</Field>

					{isTypeSelected && (
						<Fragment>
							{/* Description textarea */}
							<Field label={fieldLabels[type]} isRequired name="description">
								{({ fieldProps }) => (
									<Fragment>
										<TextArea
											{...fieldProps}
											name="feedback-description"
											minimumRows={6}
											onChange={(e) => {
												setDescription(e.target.value);
												if (validationErrors.description) {
													setValidationErrors((prev) => ({
														...prev,
														description: undefined,
													}));
												}
											}}
											value={description}
										/>
										{fg('twg_microsite_accessibility_improvements') ? (
											<MessageWrapper>
												{validationErrors.description && (
													<ErrorMessage>{validationErrors.description}</ErrorMessage>
												)}
											</MessageWrapper>
										) : (
											validationErrors.description && (
												<ErrorMessage>{validationErrors.description}</ErrorMessage>
											)
										)}
									</Fragment>
								)}
							</Field>

							{/* Attachment upload */}
							<Field name="attachments" label={formatMessage(messages.attachmentsLabel)}>
								{({ fieldProps }) => (
									<div>
										<input
											{...fieldProps}
											ref={fileInputRef}
											type="file"
											multiple
											accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
											onChange={handleFileChange}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
											style={{ display: 'none' }}
											aria-label={formatMessage(messages.uploadAriaLabel)}
										/>
										<Button appearance="default" onClick={() => fileInputRef.current?.click()}>
											<FormattedMessage {...messages.addAttachmentsButton} />
										</Button>
										{attachments.length > 0 && (
											<ul css={attachmentListStyles}>
												{attachments.map((file, index) => (
													<li key={`${file.name}-${index}`} css={attachmentItemStyles}>
														{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
														<span>{file.name}</span>
														<Button
															appearance="subtle"
															spacing="compact"
															onClick={() => removeAttachment(index)}
															aria-label={formatMessage(messages.removeAttachmentAriaLabel, {
																fileName: file.name,
															})}
														>
															<FormattedMessage {...messages.removeAttachment} />
														</Button>
													</li>
												))}
											</ul>
										)}
									</div>
								)}
							</Field>

							{/* Contact opt-in section */}
							{isAnonymous ? (
								<Field name="anonymousFeedback">
									{() => (
										<SectionMessage
											title={formatMessage(messages.anonymousTitle)}
											appearance="information"
										>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<p>
												<FormattedMessage {...messages.anonymousBody} />
											</p>
										</SectionMessage>
									)}
								</Field>
							) : (
								<Fieldset>
									<legend aria-hidden={false} hidden>
										<FormattedMessage {...messages.optInOptionsLegend} />
									</legend>
									<Field name="can-be-contacted">
										{({ fieldProps }) => (
											<Checkbox
												{...fieldProps}
												aria-describedby={undefined}
												label={
													<Fragment>
														<FormattedMessage {...messages.canBeContactedLabel} />{' '}
														<Link
															href="https://www.atlassian.com/legal/privacy-policy"
															target="_blank"
														>
															<FormattedMessage {...messages.privacyPolicyLinkText} />
														</Link>
													</Fragment>
												}
												onChange={(event) => setCanBeContacted(event.target.checked)}
											/>
										)}
									</Field>
									<Field name="enroll-in-research-group">
										{({ fieldProps }) => (
											<Checkbox
												{...fieldProps}
												aria-describedby={undefined}
												label={<FormattedMessage {...messages.enrolInResearchLabel} />}
												onChange={(event) => setEnrollInResearchGroup(event.target.checked)}
											/>
										)}
									</Field>
								</Fieldset>
							)}
						</Fragment>
					)}
				</ModalBody>

				<ModalFooter>
					<Button appearance="subtle" onClick={onClose}>
						<FormattedMessage {...messages.cancelButton} />
					</Button>
					<Button
						appearance="primary"
						type="submit"
						isDisabled={isSubmitting}
						isLoading={isSubmitting}
						testId="customFeedbackCollectorSubmitBtn"
					>
						<FormattedMessage {...messages.submitButton} />
					</Button>
				</ModalFooter>
			</Form>
		</Modal>
	);
};
